import React, { useState, useEffect } from 'react';
import { Box, Grid, Skeleton, Pagination, Chip, Typography, Stack, LinearProgress } from '@mui/material';
import Typewriter from 'typewriter-effect';
import Tweet from './Tweet';

const TweetSkeleton = () => (
  <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 2, maxWidth: '300px' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Skeleton variant="circular" width={40} height={40} />
      <Skeleton variant="text" height={30} width="60%" />
    </Box>
    <Skeleton variant="rectangular" height={60} sx={{ mt: 1 }} />
  </Box>
);

const Tweets = ({ loading, tweets, tweetsMetadata, progress, totalTime }) => {
  const [page, setPage] = useState(1);
  const tweetsPerPage = 9;
  const availableTags = tweetsMetadata.tags;
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      setElapsedTime(0);
    }
    return () => clearInterval(interval);
  }, [loading]);

  // Messages that will be displayed based on elapsed time
  const getProgressMessage = () => {
    const messages = [
      // Initial messages (0-5s)
      "Cooking up some viral content...",
      "Brewing your tweet storm...",
      "Spinning up the tweet machine...",
      
      // Early progress (5-10s)
      "Crafting those one-liners...",
      "Building your checklist...",
      "Setting up the story...",
      
      // Mid progress (10-15s)
      "Adding some spice to your hot takes...",
      "Filling in those blanks...",
      "Dropping some knowledge bombs...",
      "Making it educational but fun...",
      
      // Progressing (15-20s)
      "Sprinkling in some humor...",
      "Adding that personal touch...",
      "Making it sound human...",
      "Ditching the corporate speak...",
      
      // Almost there (20-25s)
      "Polishing those quotes...",
      "Fine-tuning the questions...",
      "Adding the final touches...",
      "Making it tweet-worthy...",
      "Adding that viral factor...",
      "Making Elon proud...",
      "Channeling your inner meme lord...",
      
      // Final stretch (25s+)
      "Almost ready to go viral...",
      "Your tweets are looking fire...",
      "Ready to make some noise...", 
      "Time to take Twitter by storm...",
      "Preparing for internet fame...",
      "Loading clout protocols...",
      "Engaging maximum virality...",
      "Checking the vibe one last time...",
      "Warming up the retweet button...",
      "Summoning the Twitter gods..."
    ];

    // Change message every 3 seconds
    const messageIndex = Math.floor(elapsedTime / 3) % messages.length;
    return messages[messageIndex];
  };

  const handleChipClick = (tag) => {
    setSelectedFilters(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
    setPage(1); // Reset to the first page when filters change
  };

  // Filter tweets: show a tweet only if it contains all the selected tags.
  const filteredTweets = tweets.filter(tweet =>
    selectedFilters.every(filterTag => tweet.tags.includes(filterTag))
  );

  // Slice the filtered tweets array for pagination
  const startIndex = (page - 1) * tweetsPerPage;
  const currentTweets = filteredTweets.slice(startIndex, startIndex + tweetsPerPage);

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  return (
    <Box sx={{ mt: 4, maxWidth: '1000px', mx: 'auto' }}>
      {loading && (
        <Box sx={{ width: '100%', mb: 4 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 5,
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #30AEF0 0%, #1F8ADD 100%)',
              },
            }}
          />
          <Box sx={{ mt: 2, minHeight: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typewriter
              options={{
                strings: [getProgressMessage()],
                autoStart: true,
                loop: true,
                delay: 20,
                deleteSpeed: 0,
                cursor: '',
              }}
            />
          </Box>
        </Box>
      )}

      {/* Filtering UI */}
      {(tweets && tweets.length > 0) &&
        <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <Typography component="h2" variant="h6">
            Filter by Type
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          {availableTags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              clickable
              color={selectedFilters.includes(tag) ? "white" : "default"}
              onClick={() => handleChipClick(tag)}
            />
          ))}
        </Stack>
      </Box>
        }
    
      <Grid container spacing={1}>
        {loading
          ? Array.from({ length: tweetsPerPage }).map((_, i) => (
              <Grid key={i} item xs={12} sm={6} md={4}>
                <TweetSkeleton />
              </Grid>
            ))
          : 
          
          currentTweets.map((tweet, i) => (
              <Grid key={i} item xs={12} sm={6} md={4}>
                <Tweet {...tweet} />
              </Grid>
            ))}
      </Grid>
      {/* Render Pagination only when not loading and when there are more tweets than one page */}
      {!loading && filteredTweets.length > tweetsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={Math.ceil(filteredTweets.length / tweetsPerPage)}
            page={page}
            color="primary"
            onChange={handleChangePage}
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
};

export default Tweets;
